import React, { useState, useEffect } from 'react';
import MapComponent from '../MapComponent';
import { Grid, Paper, Typography, Tabs, Tab, Box, Snackbar, Alert, Button } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';

// Hooks
import { useFarms } from '../hooks/useFarms';
import { useStands } from '../hooks/useStands';
import { useActivities } from '../hooks/useActivities';
import { useUserSettings } from '../hooks/useUserSettings';

// Components
import FarmSelector from './FarmSetupTab/FarmSelector';
import FarmBoundaryEditor from './FarmSetupTab/FarmBoundaryEditor';
import StandDetailsPanel from './FarmSetupTab/StandDetailsPanel';
import ActivityFilters from './ActivitiesTab/ActivityFilters';
import ActivityList from './ActivitiesTab/ActivityList';
import ActivityTimeline from './ActivitiesTab/ActivityTimeline';
import ActivityDialog from './ActivitiesTab/ActivityDialog';
import SettingsTab from './SettingsTab/SettingsTab';
import SimulationTab from './SimulationTab/SimulationTab';

const PolygonEditor = () => {
    const [message, setMessage] = useState({ text: '', type: 'info', open: false });
    const [mode, setMode] = useState('view');
    const [isEditingFarm, setIsEditingFarm] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [drawData, setDrawData] = useState({ type: 'FeatureCollection', features: [] });
    const [selectedFeature, setSelectedFeature] = useState(null);

    // Orchestrate state with custom hooks
    const farmState = useFarms(setMessage, setMode, setIsEditingFarm);
    const standState = useStands(farmState.farmId, setMessage, setMode);
    const activityState = useActivities(farmState.farmId, setMessage);
    const settingsState = useUserSettings(setMessage);

    useEffect(() => {
        farmState.loadFarms();
        settingsState.loadUserSettings();
    }, []);

    useEffect(() => {
        if (farmState.farmId) {
            standState.loadStands(farmState.farmId);
            activityState.loadActivities(farmState.farmId);
        }
    }, [farmState.farmId]);

    const handlePolygonUpdate = (totalArea, data) => {
        setDrawData(data);
        if (data.features.length > 0) setSelectedFeature(data.features[0]);
    };

    return (
        <Grid container spacing={3} style={{ padding: '2rem' }}>
            <Grid item xs={12}>
                <Paper elevation={3} style={{ padding: '1rem' }}>
                    <Typography variant="h4" gutterBottom align="center">
                        Skogskalkyl - {farmState.farmName || 'New Farm'}
                    </Typography>
                </Paper>
            </Grid>

            {/* Map Column */}
            <Grid item xs={12} md={8}>
                <Paper elevation={3} style={{ padding: '1rem', minHeight: '600px' }}>
                    <MapComponent 
                        onPolygonUpdate={handlePolygonUpdate} 
                        onPolygonSelect={(f) => setSelectedFeature(f)}
                        mode={mode}
                        farmBoundary={farmState.farmBoundary}
                        forestStands={standState.forestStands}
                        onForestStandCreate={(f, a) => standState.handleForestStandCreate(f, a, 0)}
                        onForestStandSelect={standState.handleForestStandSelect}
                        selectedStandId={standState.selectedStand?.id}
                        highlightedStandIds={activityState.highlightedStandIds}
                    />
                </Paper>
            </Grid>

            {/* Controls Column */}
            <Grid item xs={12} md={4}>
                <Paper elevation={3} style={{ minHeight: '600px', display: 'flex', flexDirection: 'column' }}>
                    <Tabs 
                        value={activeTab} 
                        onChange={(_, v) => setActiveTab(v)} 
                        indicatorColor="primary" 
                        textColor="primary" 
                        variant="scrollable" 
                        scrollButtons="auto"
                    >
                        <Tab label="Set up my farm" />
                        <Tab label="Overview" />
                        <Tab label="Activities" />
                        <Tab label="Simulation" />
                        <Tab icon={<SettingsIcon />} aria-label="settings" />
                    </Tabs>

                    <Box p={3} style={{ flexGrow: 1, overflowY: 'auto' }}>
                        {activeTab === 0 && (
                            <Box>
                                <FarmSelector 
                                    farms={farmState.farms}
                                    selectedFarmId={farmState.selectedFarmId}
                                    handleFarmSelect={farmState.handleFarmSelect}
                                    farmName={farmState.farmName}
                                    setFarmName={farmState.setFarmName}
                                    isEditingFarm={isEditingFarm}
                                />
                                <FarmBoundaryEditor 
                                    farmArea={farmState.farmArea}
                                    selectedFeature={selectedFeature}
                                    isEditingFarm={isEditingFarm}
                                    setIsEditingFarm={setIsEditingFarm}
                                    setMode={setMode}
                                    handleSaveFarm={(f) => farmState.handleSaveFarm(f, drawData)}
                                    mode={mode}
                                    setSelectedFeature={setSelectedFeature}
                                />
                                <StandDetailsPanel 
                                    selectedStand={standState.selectedStand}
                                    standDetails={standState.standDetails}
                                    setStandDetails={standState.setStandDetails}
                                    standSpecies={standState.standSpecies}
                                    setStandSpecies={standState.setStandSpecies}
                                    handleUpdateStand={standState.handleUpdateStand}
                                    handleDeleteStand={standState.handleDeleteStand}
                                    activities={activityState.activities}
                                    handleActivityClick={(a) => activityState.handleActivityClick(a, setActiveTab)}
                                    handleViewAllForStand={(sid) => activityState.handleViewAllForStand(sid, setActiveTab)}
                                />
                            </Box>
                        )}

                        {activeTab === 1 && (
                            <Typography variant="body1" align="center" style={{ marginTop: '2rem', fontStyle: 'italic' }}>
                                Overview tab - Coming soon in Phase 8
                            </Typography>
                        )}

                        {activeTab === 2 && (
                            <Box>
                                <Button 
                                    fullWidth variant="contained" startIcon={<AddIcon />} 
                                    onClick={() => activityState.handleOpenActivityDialog(null, standState.selectedStand?.id)}
                                    sx={{ mb: 2 }}
                                >
                                    Plan New Activity
                                </Button>
                                <ActivityFilters 
                                    filters={activityState.filters}
                                    toggleFilter={activityState.toggleFilter}
                                    clearFilters={activityState.clearFilters}
                                    activities={activityState.activities}
                                    uniqueStandsForFilter={activityState.uniqueStandsForFilter}
                                />
                                <ActivityTimeline 
                                    timelineYears={activityState.timelineYears}
                                    activities={activityState.filteredActivities}
                                    handleActivityClick={(a) => activityState.handleActivityClick(a, setActiveTab)}
                                    highlightedStandIds={activityState.highlightedStandIds}
                                />
                                <ActivityList 
                                    filteredActivities={activityState.filteredActivities}
                                    handleOpenActivityDialog={activityState.handleOpenActivityDialog}
                                    handleActivityClick={(a) => activityState.handleActivityClick(a, setActiveTab)}
                                    highlightedStandIds={activityState.highlightedStandIds}
                                />
                            </Box>
                        )}

                        {activeTab === 3 && (
                             <SimulationTab 
                                 forestStands={standState.forestStands} 
                                 activities={activityState.activities} 
                             />
                        )}

                        {activeTab === 4 && (
                            <SettingsTab 
                                userSettings={settingsState.userSettings}
                                setUserSettings={settingsState.setUserSettings}
                                updateUserSettings={settingsState.updateUserSettings}
                            />
                        )}
                    </Box>
                </Paper>
            </Grid>

            {/* Modals & Feedback */}
            <ActivityDialog 
                open={activityState.activityDialogOpen}
                onClose={activityState.handleCloseActivityDialog}
                onSave={activityState.handleSaveActivity}
                editingActivity={activityState.editingActivity}
                activityForm={activityState.activityForm}
                setActivityForm={activityState.setActivityForm}
                forestStands={standState.forestStands}
                financialItems={activityState.financialItems}
                handleFinancialItemChange={activityState.handleFinancialItemChange}
                handleRemoveFinancialItem={activityState.handleRemoveFinancialItem}
                handleAddFinancialItem={activityState.handleAddFinancialItem}
                calculationParams={activityState.calculationParams}
                handleParamChange={(k, v) => activityState.handleParamChange(k, v, standState.forestStands, settingsState.userSettings)}
                updateSuggestedFinancials={activityState.updateSuggestedFinancials}
                userSettings={settingsState.userSettings}
            />

            <Snackbar 
                open={message.open} 
                autoHideDuration={6000} 
                onClose={() => setMessage({ ...message, open: false })}
            >
                <Alert severity={message.type} sx={{ width: '100%' }}>{message.text}</Alert>
            </Snackbar>
        </Grid>
    );
};

export default PolygonEditor;
